import {
	Input,
	Typography,
	Card,
	Flex,
	Checkbox,
	Tabs,
	Badge,
	Empty,
	Descriptions
} from 'antd'
import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { searchStore } from '../store/search'
import { SearchResult } from '../helpers/types/search'
import { RNRelayType, RNRelay } from '../helpers/routeNames'

const { Title } = Typography

type FilterType = 'relayTypes' | 'relays' | 'relayOnes'

const filterOptions = [
	{ label: 'Типы реле', value: 'relayTypes' as FilterType },
	{ label: 'Реле', value: 'relays' as FilterType },
	{ label: 'Экземпляры реле', value: 'relayOnes' as FilterType }
]

export const WorkerHome: FC = () => {
	const { searchAll } = searchStore()
	const navigate = useNavigate()

	const [query, setQuery] = useState<string>('')
	const [result, setResult] = useState<SearchResult>()
	const [activeFilters, setActiveFilters] = useState<FilterType[]>([
		'relayTypes',
		'relays',
		'relayOnes'
	])

	const search = async (value: string) => {
		// if (value.length < 3) return
		const filterType = activeFilters.join(',')
		const data = await searchAll(value, filterType)
		setResult(data)
		setQuery('')
	}

	const handleFilterChange = (checkedValues: FilterType[]) => {
		if (checkedValues.length === 0) {
			setActiveFilters(['relayTypes', 'relays', 'relayOnes'])
		} else {
			setActiveFilters(checkedValues)
		}
	}

	const getFilteredResults = () => {
		if (!result) return null

		return {
			relayTypes: activeFilters.includes('relayTypes')
				? result.relayTypes
				: [],
			relays: activeFilters.includes('relays')
				? result.relays
				: [],
			relayOnes: activeFilters.includes('relayOnes')
				? result.relayOnes
				: []
		}
	}

	const filteredResult = getFilteredResults()

	const hasResults =
		filteredResult &&
		(filteredResult.relayTypes.length > 0 ||
			filteredResult.relays.length > 0 ||
			filteredResult.relayOnes.length > 0)

	const buildTabItems = () => {
		const items = []

		if (activeFilters.includes('relayTypes') && filteredResult) {
			items.push({
				key: 'relayTypes',
				label: (
					<Badge
						count={filteredResult.relayTypes.length || 0}
						showZero
						offset={[10, 0]}>
						<span style={{ paddingRight: 12 }}>
							Типы реле
						</span>
					</Badge>
				),
				children:
					filteredResult.relayTypes.length > 0 ? (
						<Flex vertical gap={12}>
							{filteredResult.relayTypes.map((item) => (
								<Card
									key={item.id}
									hoverable
									onClick={() =>
										navigate(
											`${RNRelayType}/${item.id}`
										)
									}
									size="small">
									<div>{item.name}</div>
								</Card>
							))}
						</Flex>
					) : (
						<Empty description="Типы реле не найдены" />
					)
			})
		}

		if (activeFilters.includes('relays') && filteredResult) {
			items.push({
				key: 'relays',
				label: (
					<Badge
						count={filteredResult.relays.length || 0}
						showZero
						offset={[10, 0]}>
						<span style={{ paddingRight: 12 }}>Реле</span>
					</Badge>
				),
				children:
					filteredResult.relays.length > 0 ? (
						<Flex vertical gap={12}>
							{filteredResult.relays.map((item) => (
								<Card
									key={item.id}
									hoverable
									onClick={() =>
										navigate(
											`${RNRelay}/${item.id}`
										)
									}
									size="small">
									<div>{item.name}</div>
								</Card>
							))}
						</Flex>
					) : (
						<Empty description="Реле не найдены" />
					)
			})
		}

		if (activeFilters.includes('relayOnes') && filteredResult) {
			items.push({
				key: 'relayOnes',
				label: (
					<Badge
						count={filteredResult.relayOnes.length || 0}
						showZero
						offset={[10, 0]}>
						<span style={{ paddingRight: 12 }}>
							Экземпляры реле
						</span>
					</Badge>
				),
				children:
					filteredResult.relayOnes.length > 0 ? (
						<Flex vertical gap={12}>
							{filteredResult.relayOnes.map((item) => (
								<Card
									key={item.id}
									hoverable
									onClick={() =>
										navigate(
											`${RNRelay}/${item.relayId}`
										)
									}
									size="small">
									<Descriptions
										size="small"
										column={1}
										title={item.relay.name}
										items={[
											{
												label: 'Уникальный номер',
												children:
													item.relayUnique
											},
											{
												label: 'Год выпуска',
												children:
													item.madeYear
											}
										]}
									/>
								</Card>
							))}
						</Flex>
					) : (
						<Empty description="Экземпляры реле не найдены" />
					)
			})
		}

		return items
	}

	const tabItems = buildTabItems()

	return (
		<Flex vertical gap={16} style={{ padding: '10px' }}>
			<Title level={3} style={{ marginBottom: 0 }}>
				Главная
			</Title>

			<Card size="small" title="Фильтры поиска">
				<Checkbox.Group
					value={activeFilters}
					onChange={handleFilterChange}
					style={{ width: '100%' }}>
					<Flex gap={8} wrap="wrap">
						{filterOptions.map((option) => (
							<Checkbox
								key={option.value}
								value={option.value}
								style={{
									padding: '4px 12px',
									border: '1px solid #d9d9d9',
									borderRadius: '16px',
									background:
										activeFilters.includes(
											option.value
										)
											? '#1890ff'
											: '#fff',
									color: activeFilters.includes(
										option.value
									)
										? '#fff'
										: 'rgba(0, 0, 0, 0.88)',
									transition: 'all 0.3s ease'
								}}>
								{option.label}
							</Checkbox>
						))}
					</Flex>
				</Checkbox.Group>
			</Card>

			<Input.Search
				placeholder="Введите запрос (минимум 3 символа)"
				onSearch={search}
				value={query}
				onChange={(e) => setQuery(e.target.value)}
				enterButton="Поиск"
				size="large"
				allowClear
			/>

			{result && (
				<Card
					title={`Результаты поиска: "${result.query}"`}
					bordered={false}>
					{hasResults ? (
						<Tabs
							defaultActiveKey="relayTypes"
							items={tabItems}
							size="large"
						/>
					) : (
						<Empty
							description="Ничего не найдено"
							style={{ padding: '40px 0' }}
						/>
					)}
				</Card>
			)}
		</Flex>
	)
}
export default WorkerHome
